import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError } from "../../api/http";
import { useAuth } from "../../auth/AuthContext";
import { Button, Input, PasswordInput, Label } from "../../components/ui";
import { useT } from "../../i18n";
import { AuthLayout } from "./AuthLayout";

export function LoginPage() {
  const t = useT();
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [preAuth, setPreAuth] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await login(email, password);
      if (res.requires_2fa) {
        setPreAuth(res.pre_auth_token ?? "");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await verify2fa(preAuth as string, code);
      navigate("/dashboard");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setBusy(false);
    }
  };

  if (preAuth !== null) {
    return (
      <AuthLayout title={t("auth.2fa.title")} subtitle={t("auth.2fa.subtitle")}>
        <form onSubmit={onVerify} className="space-y-4">
          <div>
            <Label htmlFor="code">{t("auth.2fa.code")}</Label>
            <Input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          {error && <p role="alert" className="text-sm text-danger">{error}</p>}
          <Button type="submit" loading={busy} className="w-full">
            {t("auth.2fa.verify")}
          </Button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t("auth.login.title")} subtitle={t("auth.login.subtitle")}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="password">{t("auth.password")}</Label>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            showLabel={t("common.showPassword")}
            hideLabel={t("common.hidePassword")}
          />
        </div>
        {error && <p role="alert" className="text-sm text-danger">{error}</p>}
        <Button type="submit" loading={busy} className="w-full">
          {t("auth.login")}
        </Button>
        <p className="text-center">
          <Link to="/forgot-password" className="text-sm font-medium text-brand hover:underline">
            {t("auth.forgot")}
          </Link>
        </p>
        <p className="text-center text-sm text-muted">
          {t("auth.newHere")}{" "}
          <Link to="/signup" className="font-medium text-brand hover:underline">
            {t("auth.createAccount")}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
